import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '@/store/projectStore';
import { useVersionStore } from '@/store/versionStore';
import { attachmentManager } from '@/services/attachmentManager';
import type { Attachment } from '@/models/Attachment';
import { Sidebar, SidebarToggle } from '@/components/layout/Sidebar';
import PromptEditor, { PromptEditorRef } from '@/components/editor/PromptEditor';
import { useUiStore } from '@/store/uiStore';
import { useTranslation } from '@/i18n/I18nContext';
import type { Version } from '@/models/Version';
import { MinimalButton } from '@/components/common/MinimalButton';
import VersionCanvas from '@/components/canvas/VersionCanvas';
import { AttachmentGallery } from '@/components/version/AttachmentGallery';
import { VersionMetaCard } from '@/components/version/VersionMetaCard';
import { CompareModal } from '@/components/version/CompareModal';

import { DuplicateDialog } from '@/components/common/DuplicateDialog';
import { ResizableSplitter } from '@/components/common/ResizableSplitter';
import { VerticalResizableSplitter } from '@/components/common/VerticalResizableSplitter';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { Icons } from '@/components/icons/Icons';

const SaveIcon = () => (
  <Icons.Save className="w-5 h-5" />
);

const SaveNewIcon = () => (
  <Icons.SaveNew className="w-5 h-5" />
);

const MainView: React.FC = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const { currentProjectId } = useProjectStore();
  const {
    versions,
    currentVersionId,
    loadVersions,
    createVersion,
    updateVersionInPlace,
    setCurrentVersion,
    compareState,
    compareMode,
  } = useVersionStore();
  
  // 布局偏好设置
  const {
    layoutPreference,
    setCanvasRatio,
    setEditorHeightRatio,
    startDragging,
    stopDragging,
    sidebarCollapsed,
    sidebarTemporarilyExpanded,
    setTemporarilyExpanded,
  } = useUiStore();

  const [editorContent, setEditorContent] = useState('');
  const [versionName, setVersionName] = useState('');
  const [canSaveInPlace, setCanSaveInPlace] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  
  // 编辑区容器的 ref，用于垂直分隔条计算
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const mainSplitContainerRef = useRef<HTMLDivElement>(null);
  
  // 编辑器的 ref，用于聚焦
  const editorRef = useRef<PromptEditorRef>(null);
  
  // 版本名称输入框的 ref，用于焦点切换
  const versionNameInputRef = useRef<HTMLInputElement>(null);

  // 标题栏容器 ref，用于响应式计算
  const toolbarRef = useRef<HTMLDivElement>(null);

  // 重复提醒对话框状态
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateVersion, setDuplicateVersion] = useState<Version | null>(null);
  const [pendingSaveData, setPendingSaveData] = useState<{
    projectId: string;
    content: string;
    parentId: string | null;
  } | null>(null);

  // 附件区域拖拽状态
  const [isDraggingAttachments, setIsDraggingAttachments] = useState(false);

  // 面板折叠状态 (不持久化)
  // 宽屏 (>= 1024px) 默认展开，窄屏 (< 1024px) 默认折叠
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(() => window.innerWidth < 1024);
  const [isBottomPanelCollapsed, setIsBottomPanelCollapsed] = useState(() => window.innerWidth < 1024);

  // 处理版本树中的节点点击，考虑对比模式
  const handleVersionNodeClick = (versionId: string) => {
    if (compareMode && compareState.sourceVersionId && versionId !== compareState.sourceVersionId) {
      // 在对比模式下，点击不同版本则进行对比
      // 不改变当前选中的版本，保持对比前的状态
      useVersionStore.getState().setCompareTarget(versionId);
    } else {
      // 非对比模式或点击同一版本，则正常切换版本
      setCurrentVersion(versionId);
    }
  };

  // 加载项目的版本
  useEffect(() => {
    if (currentProjectId) {
      // 切换项目时，先清空当前版本ID
      setCurrentVersion(null);
      loadVersions(currentProjectId);
      
      // 聚焦到编辑器
      setTimeout(() => {
        editorRef.current?.focus();
      }, 200); // 稍微延迟一点时间，确保版本加载完成
    }
  }, [currentProjectId, loadVersions, setCurrentVersion]);

  // 页面加载时的临时展开逻辑
  useEffect(() => {
    // 仅在页面首次加载且没有选中项目时执行
    if (!currentProjectId && !sidebarTemporarilyExpanded) {
      // 如果LocalStorage中侧边栏是折叠状态，临时展开它
      if (sidebarCollapsed) {
        setTemporarilyExpanded(true);
      }
    }
  }, [currentProjectId, sidebarCollapsed, sidebarTemporarilyExpanded, setTemporarilyExpanded]);

  // 项目选择后的自动折叠逻辑
  useEffect(() => {
    // 当用户选择了项目且之前是临时展开状态时，取消临时展开
    if (currentProjectId && sidebarTemporarilyExpanded) {
      setTemporarilyExpanded(false);
    }
  }, [currentProjectId, sidebarTemporarilyExpanded, setTemporarilyExpanded]);

  // 更新编辑器内容和附件，自动选择最近更新的版本
  useEffect(() => {
    if (currentProjectId && versions.length > 0) {
      // 如果没有选中版本，或当前选中的版本不属于当前项目，自动选择最近更新的版本
      const currentVersion = currentVersionId ? versions.find(v => v.id === currentVersionId) : null;
      if (!currentVersionId || !currentVersion || currentVersion.projectId !== currentProjectId) {
        const projectVersions = versions.filter(v => v.projectId === currentProjectId);
        // 按updatedAt降序排序，获取最近更新的版本
        const sortedVersions = [...projectVersions].sort((a, b) => b.updatedAt - a.updatedAt);
        if (sortedVersions.length > 0) {
          setCurrentVersion(sortedVersions[0].id);
        }
      }
    }
  }, [currentProjectId, versions, currentVersionId, setCurrentVersion]);

  // 加载当前版本内容
  useEffect(() => {
    if (currentVersionId && currentProjectId) {
      const version = versions.find((v) => v.id === currentVersionId);
      if (version) {
        setEditorContent(version.content);
        setVersionName(version.name || ''); // 加载版本名称
        
        // User Story 4: 所有版本都可以原地保存
        setCanSaveInPlace(true);

        // 加载附件
        loadAttachments(currentVersionId);
        
        // 聚焦到编辑器
        setTimeout(() => {
          editorRef.current?.focus();
        }, 100);
      }
    } else {
      setEditorContent('');
      setVersionName('');
      setAttachments([]);
      setCanSaveInPlace(false);
      
      // 聚焦到编辑器
      setTimeout(() => {
        editorRef.current?.focus();
      }, 100);
    }
  }, [currentVersionId, versions, currentProjectId]);

  const loadAttachments = async (versionId: string) => {
    try {
      const att = await attachmentManager.getAttachmentsByVersion(versionId);
      setAttachments(att);
    } catch (error) {
      console.error(t('pages.mainView.errors.loadAttachmentsFailed'), error);
    }
  };

  const handleSave = async () => {
    if (!currentProjectId) {
      alert(t('pages.mainView.errors.selectProjectFirst'));
      return;
    }

    try {
      const versionId = await createVersion(
        currentProjectId,
        editorContent,
        currentVersionId,
        true, // 跳过重复检测
        versionName
      );
      setCurrentVersion(versionId);
      await loadVersions(currentProjectId);
    } catch (error) {
      alert(`${t('pages.mainView.errors.saveFailed')}: ${error}`);
    }
  };

  const handleConfirmDuplicateCreate = async () => {
    // 此函数已不再使用，因为移除了重复检测功能
    // 保留以防万一
    if (!pendingSaveData) return;

    try {
      // 强制创建(跳过重复检测)
      const versionId = await createVersion(
        pendingSaveData.projectId,
        pendingSaveData.content,
        pendingSaveData.parentId,
        true, // 跳过重复检测
        versionName
      );
      setCurrentVersion(versionId);
      await loadVersions(pendingSaveData.projectId);
      
      // 清理状态
      setShowDuplicateDialog(false);
      setDuplicateVersion(null);
      setPendingSaveData(null);
    } catch (error) {
      alert(`${t('pages.mainView.errors.saveFailed')}: ${error}`);
    }
  };

  const handleCancelDuplicateCreate = () => {
    setShowDuplicateDialog(false);
    setDuplicateVersion(null);
    setPendingSaveData(null);
  };

  const handleSaveInPlace = async () => {
    if (!currentVersionId) {
      alert(t('pages.mainView.errors.selectVersionFirst'));
      return;
    }

    // User Story 4: 允许所有版本原地保存，不需要任何提示
    try {
      await updateVersionInPlace(currentVersionId, editorContent, versionName);
      await loadVersions(currentProjectId!);
    } catch (error) {
      alert(`${t('pages.mainView.errors.saveFailed')}: ${error}`);
    }
  };

  const handleUploadFiles = async (files: FileList) => {
    setIsDraggingAttachments(false);
    if (!currentVersionId) return;

    const validTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/webm',
    ];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // 验证文件类型
      if (!validTypes.includes(file.type)) {
        alert(`${t('components.attachmentGallery.unsupportedType')}: ${file.type}`);
        continue;
      }

      // 验证文件大小（50MB）
      if (file.size > 50 * 1024 * 1024) {
        alert(`${t('components.attachmentGallery.fileTooLarge')}: ${file.name}`);
        continue;
      }

      try {
        await attachmentManager.uploadAttachment(currentVersionId, file);
      } catch (error) {
        console.error('上传附件失败:', error);
        alert(`${t('components.attachmentGallery.uploadFailed')}: ${file.name}`);
      }
    }
    loadAttachments(currentVersionId);
  };

  const handleAttachmentDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingAttachments(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUploadFiles(e.dataTransfer.files);
    }
  };

  const handleAttachmentDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingAttachments(true);
  };

  const handleAttachmentDragLeave = () => {
    setIsDraggingAttachments(false);
  };

  return (
    <div className="h-dynamic-screen flex flex-col bg-surface">
      {/* 顶部标题栏 */}
      <header className="bg-primary text-onPrimary px-6 py-1 shadow-m3-1 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Prompt Studio</h1>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a
            href="https://github.com/JoeyLearnsToCode/prompt-studio"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-full hover:bg-onPrimary/20 transition-colors"
            aria-label="GitHub Repository"
          >
            <Icons.GitHub className="h-6 w-6" />
          </a>
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-full hover:bg-onPrimary/20 transition-colors"
            aria-label={t('common.settings')}
          >
          <Icons.Settings className="h-6 w-6" />
          </button>
        </div>
      </header>

      {/* 主要内容区域 */}
      <div className="flex-1 flex overflow-hidden">
        {/* 左侧边栏 */}
        <Sidebar />

        {/* 主要内容包装器，用于隔离左侧边栏，确保分隔条比例计算正确 */}
        <div className="flex-1 flex overflow-hidden w-0 min-w-0" ref={mainSplitContainerRef}>
        {/* 中央编辑区 */}
        <div 
          className="flex flex-col"
            style={{ width: isRightPanelCollapsed ? '100%' : `${layoutPreference.canvasPanelWidthRatio * 100}%` }}
          >
            {sidebarCollapsed && !sidebarTemporarilyExpanded && (!currentProjectId || !currentVersionId) && (
              <div className="px-4 py-3">
                <SidebarToggle />
              </div>
            )}

            {/* 版本名称输入框 */}
            {currentProjectId && currentVersionId && (
              <div ref={toolbarRef} className="toolbar px-4 py-3 bg-surface-variant border-b border-surface-onVariant/20 @container">
                <div className="flex items-center gap-2 h-10">
                  {sidebarCollapsed && !sidebarTemporarilyExpanded && (<div className="flex-shrink-0"><SidebarToggle /></div>)}
                  <label
                    htmlFor="version-name"
                    className="text-sm font-medium text-surface-onVariant whitespace-nowrap overflow-hidden text-ellipsis min-w-0"
                    style={{ flexShrink: 3 }}
                    title={t('pages.mainView.versionName')}
                  >
                    <span className="hidden @sm:inline">{t('pages.mainView.versionName') + ':'}</span>
                  </label>
                  <input
                    ref={versionNameInputRef}
                    id="version-name"
                    type="text"
                    value={versionName}
                    onChange={(e) => setVersionName(e.target.value)}
                    onKeyDown={(e) => {
                      // 处理保存快捷键
                      if (e.ctrlKey && e.key === 'Enter') {
                        if (e.shiftKey) {
                          // Ctrl+Shift+Enter: 保存新版本
                          handleSave();
                        } else {
                          // Ctrl+Enter: 原地保存
                          handleSaveInPlace();
                        }
                      } else if (e.ctrlKey && e.key === 's') {
                        e.preventDefault();
                        if (e.shiftKey) {
                          // Ctrl+Shift+S: 保存新版本
                          handleSave();
                        } else {
                          // Ctrl+S: 原地保存
                          handleSaveInPlace();
                        }
                      } else if (e.key === 'Tab' && !e.shiftKey) {
                        // Tab: 切换到编辑器
                        e.preventDefault();
                        editorRef.current?.focus();
                      }
                    }}
                    placeholder={t('pages.mainView.versionNamePlaceholder')}
                    className="flex-1 px-2 py-2 text-sm bg-surface border border-surface-onVariant/30 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary min-w-[10px]"
                    style={{ flexShrink: 1 }}
                  />

                  {/* 保存按钮 */}
                  <MinimalButton
                    onClick={handleSaveInPlace}
                    disabled={!canSaveInPlace || !currentProjectId}
                    title={`${t('components.toolbar.saveInPlace')} (Ctrl+S / Ctrl+Enter)`}
                    className="whitespace-nowrap flex-shrink-0 px-2 py-1 text-sm"
                  >
                    <span className="inline @xs:hidden"><SaveIcon /></span> <span className="hidden @xs:inline">{t('components.toolbar.saveInPlace')}</span>
                  </MinimalButton>

                  <MinimalButton
                    onClick={handleSave}
                    disabled={!currentProjectId}
                    title={`${t('components.toolbar.saveNew')} (Ctrl+Shift+S / Ctrl+Shift+Enter)`}
                    className="whitespace-nowrap flex-shrink-0 px-2 py-1 text-sm"
                  >
                    <span className="inline @xs:hidden"><SaveNewIcon /></span> <span className="hidden @xs:inline">{t('components.toolbar.saveNew')}</span>
                  </MinimalButton>
                </div>
              </div>
            )}

          <div className="flex-1 flex flex-col overflow-hidden" ref={editorContainerRef}>
            {currentProjectId ? (
              <>
                <div 
                  className="overflow-hidden"
                    style={{ height: isBottomPanelCollapsed ? '100%' : `${layoutPreference.editorHeightRatio * 100}%` }}
                >
                  <PromptEditor
                    ref={editorRef}
                    value={editorContent}
                    onChange={setEditorContent}
                    onSave={handleSave}
                    onSaveInPlace={handleSaveInPlace}
                    onFocusVersionName={() => versionNameInputRef.current?.focus()}
                  />
                </div>
                
                {/* 垂直分隔条 */}
                {currentVersionId && (
                  <VerticalResizableSplitter
                    ratio={layoutPreference.editorHeightRatio}
                    onRatioChange={setEditorHeightRatio}
                    onDragStart={startDragging}
                    onDragEnd={stopDragging}
                    minRatio={0.3}
                    maxRatio={0.9}
                    containerRef={editorContainerRef}
                      isCollapsed={isBottomPanelCollapsed}
                      onCollapse={() => setIsBottomPanelCollapsed(!isBottomPanelCollapsed)}
                  />
                )}
                
                {/* 附件区域 */}
                {currentVersionId && (
                  <div 
                    className={`p-4 overflow-y-auto transition-colors duration-200 ${isDraggingAttachments
                      ? 'bg-primary-container/30 border-2 border-dashed border-primary'
                      : 'bg-surface-container-low'
                      }`}
                      style={{
                        height: isBottomPanelCollapsed ? '0px' : `${(1 - layoutPreference.editorHeightRatio) * 100}%`,
                        display: isBottomPanelCollapsed ? 'none' : 'block'
                      }}
                    onDrop={handleAttachmentDrop}
                    onDragOver={handleAttachmentDragOver}
                    onDragLeave={handleAttachmentDragLeave}
                  >
                    <h3 className="text-sm font-semibold mb-3">{t('pages.mainView.attachments')}</h3>
                    <AttachmentGallery
                      versionId={currentVersionId}
                      attachments={attachments}
                      onAttachmentsChange={() => loadAttachments(currentVersionId)}
                      readonly={false}
                      onUpload={handleUploadFiles}
                      extraCard={
                        <VersionMetaCard
                          versionId={currentVersionId}
                          score={versions.find(v => v.id === currentVersionId)?.score}
                          notes={versions.find(v => v.id === currentVersionId)?.notes}
                          readonly={false}
                        />
                      }
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-surface-onVariant">
                <div className="text-center">
                  <p className="text-xl mb-2">👈 {t('pages.mainView.noProject')}</p>
                  <p className="text-sm">{t('pages.mainView.noProjectHint')}</p>
                </div>
              </div>
            )}
          </div>
        </div>

          {/* 可拖动分隔符 */}
          <ResizableSplitter
            ratio={layoutPreference.canvasPanelWidthRatio}
            onRatioChange={setCanvasRatio}
            onDragStart={startDragging}
            onDragEnd={stopDragging}
            minRatio={0.2}
            maxRatio={0.8}
            containerRef={mainSplitContainerRef}
            isCollapsed={isRightPanelCollapsed}
            onCollapse={() => setIsRightPanelCollapsed(!isRightPanelCollapsed)}
          />

        {/* 右侧画布区 - 版本树可视化 */}
        <div 
          className="border-l border-surface-onVariant/20 overflow-hidden"
            style={{
              width: isRightPanelCollapsed ? '0px' : `${(1 - layoutPreference.canvasPanelWidthRatio) * 100}%`,
              display: isRightPanelCollapsed ? 'none' : 'block'
            }}
        >
          <VersionCanvas
            projectId={currentProjectId}
            onNodeClick={handleVersionNodeClick}
            hasProject={!!currentProjectId}
            isCollapsed={isRightPanelCollapsed}
          />
        </div>
        </div>
      </div>

      {/* 版本对比模态框 */}
      <CompareModal
        isOpen={compareState.isOpen}
        sourceVersion={versions.find(v => v.id === compareState.sourceVersionId) || null}
        targetVersion={versions.find(v => v.id === compareState.targetVersionId) || null}
        onClose={() => useVersionStore.getState().closeCompare()}
      />

      {/* 重复内容提醒对话框 */}
      <DuplicateDialog
        isOpen={showDuplicateDialog}
        duplicateVersion={duplicateVersion}
        onConfirm={handleConfirmDuplicateCreate}
        onCancel={handleCancelDuplicateCreate}
      />
    </div>
  );
};

export default MainView;